import { useEffect, useRef, useState } from 'react';
import { Mic, Square } from 'lucide-react';
import { useAudio, micConstraints } from '@/store/audio';
import clsx from 'clsx';

/**
 * Mikrofon seviye testi — MANUEL. Kullanıcı "Mikrofonu Test Et" butonuna
 * basınca mic açılır ve canlı seviye gösterilir; "Durdur" ile kapanır.
 *
 * Önceden mount olur olmaz otomatik getUserMedia + sürekli örnekleme yapıyordu
 * (Settings açıkken mic hep canlı kalıyordu). Artık sadece kullanıcı isteyince
 * mic'e erişilir — gizlilik + pil + "mic açık" hissi kontrolü kullanıcıda.
 *
 * Çalışırken micDeviceId/EC/NS değişirse stream tazelenir.
 */
export function MicLevelMeter() {
  const micDeviceId = useAudio((s) => s.micDeviceId);
  const echoCancellation = useAudio((s) => s.echoCancellation);
  const noiseSuppression = useAudio((s) => s.noiseSuppression);
  const [running, setRunning] = useState(false);
  const [level, setLevel] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const acRef = useRef<AudioContext | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!running) return;
    let stopped = false;
    setError(null);

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: micConstraints() });
        if (stopped) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;

        const AC = window.AudioContext || (window as any).webkitAudioContext;
        const ac: AudioContext = new AC();
        acRef.current = ac;
        const src = ac.createMediaStreamSource(stream);
        const analyser = ac.createAnalyser();
        analyser.fftSize = 1024;
        src.connect(analyser);
        const buf = new Uint8Array(analyser.fftSize);

        const tick = () => {
          analyser.getByteTimeDomainData(buf);
          let sum = 0;
          for (let i = 0; i < buf.length; i++) {
            const v = (buf[i] - 128) / 128;
            sum += v * v;
          }
          const rms = Math.sqrt(sum / buf.length);
          const pct = Math.min(100, Math.round(rms * 200));
          setLevel(pct);
          rafRef.current = requestAnimationFrame(tick);
        };
        tick();
      } catch (e: any) {
        console.warn('[mic-meter] getUserMedia failed', e);
        setError(e?.message ?? 'mic erişimi başarısız');
      }
    })();

    return () => {
      stopped = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      acRef.current?.close().catch(() => { /* */ });
      acRef.current = null;
      setLevel(0);
    };
  }, [running, micDeviceId, echoCancellation, noiseSuppression]);

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-[11.5px] font-medium text-slate-600 flex items-center gap-1.5">
          <Mic size={11} className="text-slate-500" />
          Mikrofon Seviyesi
        </span>
        <button
          onClick={() => setRunning((r) => !r)}
          className={clsx(
            'inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium transition active:scale-95',
            running
              ? 'bg-red-50 text-red-600 ring-1 ring-red-200 hover:bg-red-100'
              : 'bg-brand-500 text-[#08140d] hover:bg-brand-600',
          )}
        >
          {running ? <><Square size={10} /> Durdur</> : <><Mic size={11} /> Test Et</>}
        </button>
      </div>

      {/* 30-bar level meter — duruyorken tüm barlar sönük */}
      <div className="flex items-end gap-[2px] h-5">
        {Array.from({ length: 30 }).map((_, i) => {
          const threshold = (i + 1) * (100 / 30);
          const active = running && level >= threshold;
          const color = active
            ? i < 18 ? 'bg-emerald-500'
              : i < 24 ? 'bg-amber-500'
                : 'bg-red-500'
            : 'bg-slate-200';
          return (
            <span
              key={i}
              className={clsx('flex-1 rounded-sm transition-colors duration-75', color)}
              style={{ height: `${30 + (i / 29) * 70}%` }}
            />
          );
        })}
      </div>

      {!running ? (
        <p className="text-[10.5px] text-slate-400 leading-snug">
          Mikrofonunu test etmek için "Test Et"e bas — bittiğinde durdur, mic kapanır.
        </p>
      ) : error ? (
        <p className="text-[10.5px] text-red-600 leading-snug">{error}</p>
      ) : level < 5 ? (
        <p className="text-[10.5px] text-slate-500 leading-snug">
          Mikrofona konuş — barlar yeşil olmalı. Hareket etmiyorsa mic kapalı veya yanlış cihaz seçili.
        </p>
      ) : (
        <p className="text-[10.5px] text-emerald-700 leading-snug">
          Mikrofon çalışıyor ✓
        </p>
      )}
    </div>
  );
}
